var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _HznCheckbox_instances, _HznCheckbox_onKeydown, _HznCheckbox_handleClick, _HznCheckbox_renderCheckboxSVG;
import { LitElement, nothing } from '@horizon/base';
import { property, query } from '@horizon/base/decorators.js';
import { live, classMap } from '@horizon/base/directives';
import { unsafeStatic, html } from '@horizon/base/static-html.js';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { submit } from '@open-wc/form-helpers';
import { eventEmitter } from '@horizon/common/events';
import { ToggleInputMixin } from '@horizon/common/mixins';
import { HasSlotController } from '@horizon/common/controllers';
import { HznStack } from '@horizon/stack';
import { HznText } from '@horizon/text';
import checkboxStyles from './checkbox.css.js';
import { indeterminateSvg } from './indeterminate.svg.js';
import { checkedSvg } from './checked.svg.js';
/**
 *
 * @tag hzn-checkbox
 * @tagname hzn-checkbox
 * @summary A component for making checkboxes that behave as native form checkbox elements
 *
 * @fires {CustomEvent<{}>} change - Emitted when the checkbox is changed
 */
export class HznCheckbox extends ToggleInputMixin(ScopedElementsMixin(LitElement)) {
    constructor() {
        super(...arguments);
        _HznCheckbox_instances.add(this);
        this.hasSlotController = new HasSlotController(this, '[default]');
        /**
         * Set the indeterminate state and sets input.indeterminate
         */
        this.indeterminate = false;
    }
    static get scopedElements() {
        return {
            'hzn-stack': HznStack,
            'hzn-text': HznText,
        };
    }
    connectedCallback() {
        super.connectedCallback();
        ['focusin', 'mouseover'].forEach((eventName) => this.addEventListener(eventName, () => {
            var _a;
            const textEl = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('hzn-text.label');
            if (!this.checked && !this.indeterminate && !this.disabled) {
                textEl.weight = 'bold';
            }
        }));
        ['focusout', 'mouseout'].forEach((eventName) => this.addEventListener(eventName, () => {
            var _a;
            const textEl = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('hzn-text.label');
            if (!this.checked && !this.indeterminate && !this.disabled) {
                textEl.weight = 'base';
            }
        }));
        // hide default browser invalid popup
        this.addEventListener('invalid', (event) => event.preventDefault());
    }
    /**
     * @private
     */
    resetFormControl() {
        this.checked = false;
        this.value = '';
    }
    render() {
        return html `
      <hzn-stack space="xsmall">
        ${this.showErrors
            ? html `<hzn-text class="error-message mb-small" variant="caption" tone="critical"
              >${this.internalErrorMessage}</hzn-text
            >`
            : nothing}
        <label id="checkbox-label" class="hzn-checkbox" for="checkbox" >
          <input
            type="checkbox"
            name=${this.name}
            id="checkbox"
            .checked=${live(this.checked)}
            .indeterminate=${live(this.checked ? false : this.indeterminate)}
            .disabled=${this.disabled}
            ?required="${this.required}"
            .value=${this.value}
            aria-checked=${this.checked ? 'true' : this.indeterminate ? 'mixed' : 'false'}
            aria-labelledby="checkbox-label"
            aria-describedby="helper-text"
            @click=${__classPrivateFieldGet(this, _HznCheckbox_instances, "m", _HznCheckbox_handleClick)}
            @keydown=${__classPrivateFieldGet(this, _HznCheckbox_instances, "m", _HznCheckbox_onKeydown)}
          />

          <span class="checkbox-control"> ${__classPrivateFieldGet(this, _HznCheckbox_instances, "m", _HznCheckbox_renderCheckboxSVG).call(this)} </span>
          <hzn-stack space="small" class="checkbox-label">
            <hzn-text
              class="label"
              weight="${this.checked || (!this.checked && this.indeterminate) ? 'bold' : 'base'}"
              tone="${this.disabled ? 'disabled' : 'neutral'}"
            >
              <slot>${this.displayName || this.name || 'Checkbox'}</slot>
            </hzn-text>
            ${this.helperText
            ? html ` <hzn-text
            class="${this.hideHelperText ? 'visually-hidden' : ''}"
            id="helper-text"
            variant="caption"
            tone="subdued"
          >
            ${this.helperText}
          </hzn-text>`
            : nothing}
          </hzn-stack>
        </label>
      </hzn-stack>
    `;
    }
}
_HznCheckbox_instances = new WeakSet(), _HznCheckbox_onKeydown = function _HznCheckbox_onKeydown(event) {
    const hasModifier = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    // Pressing enter when focused on an input should submit the form like a native input
    if (event.code === 'Enter' && !hasModifier) {
        if (this.form) {
            submit(this.form);
        }
    }
}, _HznCheckbox_handleClick = function _HznCheckbox_handleClick(event) {
    this.checked = event.target.checked;
    if (this.checked) {
        this.indeterminate = false;
    }
    eventEmitter(this)({ type: 'change' });
}, _HznCheckbox_renderCheckboxSVG = function _HznCheckbox_renderCheckboxSVG() {
    const checkboxDefault = {
        'empty-checkbox': true,
        error: this.showErrors,
    };
    if (!this.checked && !this.indeterminate) {
        return html ` <span class="${classMap(checkboxDefault)}"> </span> `;
    }
    else if (!this.checked && this.indeterminate) {
        return html `<span class="checkbox-icon indeterminate"> ${unsafeStatic(indeterminateSvg)} </span>`;
    }
    else {
        return html `<span class="checkbox-icon checked"> ${unsafeStatic(checkedSvg)} </span>`;
    }
};
HznCheckbox.styles = [checkboxStyles];
__decorate([
    query('#checkbox')
], HznCheckbox.prototype, "innerCheckbox", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HznCheckbox.prototype, "indeterminate", void 0);
//# sourceMappingURL=checkbox.js.map